export function createRefreshScheduler(options: {
    getTokenExpiry: () => number | undefined;
    isUserActive: (thresholdMs: number) => boolean;
    refreshFn: () => Promise<boolean>;
    onRefreshed?: () => void;
    thresholdMs?: number;   // default 120_000 (2 minutes)
    intervalMs?: number;    // default 30_000 (30 seconds)
    idleThresholdMs?: number; // fallback if not provided
}) {
    let intervalId: ReturnType<typeof setInterval> | null = null;
    let isRefreshing = false;

    const thresholdMs = options.thresholdMs ?? 120_000;
    const intervalMs = options.intervalMs ?? 30_000;
    // Default to a 5-minute activity check if idle timeout is missing
    const idleThresholdMs = options.idleThresholdMs ?? 300_000;

    async function checkAndRefresh() {
        if (isRefreshing) return;

        const expiry = options.getTokenExpiry();
        if (!expiry) return;

        const now = Date.now();
        const timeUntilExpiry = expiry - now;

        // Condition: Token is near expiration
        if (timeUntilExpiry > 0 && timeUntilExpiry < thresholdMs) {
            // Condition: User is active
            if (options.isUserActive(idleThresholdMs)) {
                isRefreshing = true;
                try {
                    const success = await options.refreshFn();
                    if (success && options.onRefreshed) {
                        options.onRefreshed();
                    }
                } catch (error) {
                    console.error('[RefreshScheduler] Error during proactive refresh:', error);
                } finally {
                    isRefreshing = false;
                }
            } else {
                // User is idle. Do NOT refresh proactively. Let it expire or idle timeout.
                // console.log('[RefreshScheduler] Skipped refresh due to user idle');
            }
        }
    }

    return {
        start() {
            if (intervalId) return;
            intervalId = setInterval(checkAndRefresh, intervalMs);
        },
        stop() {
            if (intervalId) {
                clearInterval(intervalId);
                intervalId = null;
            }
        },
        async forceRefresh() {
            if (isRefreshing) return false;
            isRefreshing = true;
            try {
                const success = await options.refreshFn();
                if (success && options.onRefreshed) {
                    options.onRefreshed();
                }
                return success;
            } finally {
                isRefreshing = false;
            }
        }
    };
}
